import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, 
    FaTrashAlt, 
    FaTimes, 
    FaUpload, 
    FaSearch, 
    FaChevronDown, 
    FaChevronUp, 
    FaPlus, 
    FaExchangeAlt, 
    FaFolder, 
    FaSave, 
    FaCog
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import './AddGallery.css';
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Safe image path builder
const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${API_BASE_URL}${cleanPath}`;
};

const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

/* =========================================================
   1. FOLDER TOOLS MODAL (Rename / Delete Album Folders)
========================================================= */
const FolderToolsModal = ({ isOpen, onClose, folders, onRename, onDelete }) => {
    const [activeTab, setActiveTab] = useState('rename');
    const [selectedFolder, setSelectedFolder] = useState('');
    const [newName, setNewName] = useState('');
    const [deleteType, setDeleteType] = useState('content');

    useEffect(() => {
        if (!isOpen) {
            setSelectedFolder('');
            setNewName('');
            setDeleteType('content');
            setActiveTab('rename');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleExecute = () => {
        if (!selectedFolder) return Swal.fire('Error', 'Please select a folder.', 'error');
        if (activeTab === 'rename') {
            if (!newName.trim()) return Swal.fire('Error', 'Please enter a new folder name.', 'error');
            onRename(selectedFolder, newName.trim());
        } else {
            onDelete(selectedFolder, deleteType);
        }
    };

    return (
        <div className="admin-gallery-page-modal-overlay" onClick={onClose}>
            <div className="admin-gallery-page-modal-content tools-modal" onClick={e => e.stopPropagation()}>
                <div className="admin-gallery-page-modal-header">
                    <h2>Manage Album Folders</h2>
                    <button className="admin-gallery-page-modal-close-btn" onClick={onClose} aria-label="Close"><FaTimes /></button>
                </div>
                <div className="admin-gallery-page-tabs">
                    <button className={`admin-gallery-page-tab-btn ${activeTab === 'rename' ? 'active' : ''}`} onClick={() => setActiveTab('rename')}>
                        <FaEdit /> Rename Folder
                    </button>
                    <button className={`admin-gallery-page-tab-btn ${activeTab === 'delete' ? 'active' : ''}`} onClick={() => setActiveTab('delete')}>
                        <FaTrashAlt /> Delete Folder
                    </button>
                </div>
                <div className="admin-gallery-page-modal-body">
                    <div className="admin-gallery-page-form-group">
                        <label>Select Folder</label>
                        <select className="admin-gallery-page-select-input" value={selectedFolder} onChange={(e) => setSelectedFolder(e.target.value)}>
                            <option value="">-- Choose Folder --</option>
                            {folders.filter(Boolean).map((f, i) => <option key={i} value={f}>{f}</option>)}
                        </select>
                    </div>
                    {activeTab === 'rename' && (
                        <div className="admin-gallery-page-tab-content">
                            <div className="admin-gallery-page-form-group">
                                <label>New Folder Name</label>
                                <input type="text" className="admin-gallery-page-input" placeholder="Enter new folder name..." value={newName} onChange={(e) => setNewName(e.target.value)} />
                            </div>
                        </div>
                    )}
                    {activeTab === 'delete' && (
                        <div className="admin-gallery-page-tab-content">
                            <label className="admin-gallery-page-radio-label">
                                <input type="radio" name="delType" checked={deleteType === 'folder'} onChange={() => setDeleteType('folder')} />
                                <div className="radio-text">
                                    <strong>Delete Folder Name Only</strong>
                                    <span>Retains the photos and removes the folder grouping.</span>
                                </div>
                            </label>
                            <label className="admin-gallery-page-radio-label">
                                <input type="radio" name="delType" checked={deleteType === 'content'} onChange={() => setDeleteType('content')} />
                                <div className="radio-text">
                                    <strong>Delete Folder &amp; All Images</strong>
                                    <span>Permanently removes the folder and all photos inside.</span>
                                </div>
                            </label>
                        </div>
                    )}
                </div>
                <div className="admin-gallery-page-modal-footer">
                    <button type="button" className="admin-gallery-page-modal-btn dismiss" onClick={onClose}>Cancel</button>
                    <button type="button" className={`admin-gallery-page-modal-btn save ${activeTab === 'delete' ? 'danger' : ''}`} onClick={handleExecute}>
                        {activeTab === 'rename' ? 'Save Changes' : 'Delete Now'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* =========================================================
   2. ADD / EDIT ITEM MODAL
========================================================= */
const GalleryFormModal = ({ isOpen, onClose, data, mode, onSave, existingFolders }) => {
    const [formData, setFormData] = useState({ folderName: '', description: '', isActive: true });
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState('');
    const [isNewFolder, setIsNewFolder] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            if (data) {
                setFormData({
                    folderName: data.folderName || '',
                    description: data.description || '',
                    isActive: data.isActive !== undefined ? data.isActive : true,
                });
                setPreview(getImageUrl(data.image));
                setIsNewFolder(!existingFolders.includes(data.folderName));
                setImageFile(null);
            } else {
                setFormData({ folderName: existingFolders[0] || '', description: '', isActive: true });
                setPreview('');
                setImageFile(null);
                setIsNewFolder(existingFolders.length === 0);
            }
        }
    }, [data, isOpen, existingFolders]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveMedia = (e) => {
        e.stopPropagation();
        setImageFile(null);
        setPreview('');
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = () => {
        if (mode === 'add' && !imageFile) return Swal.fire('Error', 'Please upload a photo.', 'error');
        if (!formData.folderName.trim()) return Swal.fire('Error', 'Please provide or select a folder name.', 'error');

        const submissionData = new FormData();
        submissionData.append('folderName', formData.folderName.trim());
        submissionData.append('description', formData.description ? formData.description.trim() : '');
        submissionData.append('isActive', formData.isActive.toString());

        if (imageFile) {
            submissionData.append('image', imageFile);
        }

        onSave(submissionData);
    };

    if (!isOpen) return null;

    return (
        <div className="admin-gallery-page-modal-overlay" onClick={onClose}>
            <div className="admin-gallery-page-modal-content" onClick={e => e.stopPropagation()}>
                <div className="admin-gallery-page-modal-header">
                    <h2>{data ? 'Edit Gallery Photo' : 'Upload New Photo'}</h2>
                    <button className="admin-gallery-page-modal-close-btn" onClick={onClose} aria-label="Close"><FaTimes /></button>
                </div>
                <div className="admin-gallery-page-modal-body">
                    
                    <div className="admin-gallery-page-form-group">
                        <div className="admin-gallery-page-label-row">
                            <label>Folder / Album Name</label>
                            <span className="admin-gallery-page-toggle-link" onClick={() => { setIsNewFolder(!isNewFolder); setFormData({...formData, folderName: ''}); }}>
                                {isNewFolder ? 'Select Existing Folder' : '+ Create New Folder'}
                            </span>
                        </div>
                        {isNewFolder ? (
                            <input 
                                type="text" 
                                name="folderName" 
                                value={formData.folderName} 
                                onChange={handleChange} 
                                className="admin-gallery-page-input" 
                                placeholder="Enter folder name (e.g. Campus Labs)" 
                            />
                        ) : (
                            <select 
                                name="folderName" 
                                value={formData.folderName} 
                                onChange={handleChange} 
                                className="admin-gallery-page-select-input"
                            >
                                <option value="">-- Select Folder --</option>
                                {existingFolders.filter(Boolean).map((f, i) => <option key={i} value={f}>{f}</option>)}
                            </select>
                        )}
                    </div>

                    <div className="admin-gallery-page-form-group">
                        <label>Photo File</label>
                        <div className="admin-gallery-page-preview-box" onClick={() => fileInputRef.current.click()}>
                            {preview ? (
                                <div className="admin-gallery-page-image-container">
                                    <img src={preview} alt="Upload Preview" />
                                    <div className="admin-gallery-page-overlay">
                                        <button type="button" className="admin-gallery-page-mini-btn" title="Change Image" onClick={(e) => { e.stopPropagation(); fileInputRef.current.click(); }}>
                                            <FaExchangeAlt />
                                        </button>
                                        <button type="button" className="admin-gallery-page-mini-btn remove" title="Remove" onClick={handleRemoveMedia}>
                                            <FaTrashAlt />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="admin-gallery-page-upload-placeholder">
                                    <FaUpload size={24} />
                                    <span>Click to Browse / Upload Photo</span>
                                </div>
                            )}
                            <input type="file" ref={fileInputRef} hidden onChange={handleFileChange} accept="image/*" />
                        </div>
                    </div>

                    <div className="admin-gallery-page-form-group">
                        <label>Description / Caption (Optional)</label>
                        <textarea 
                            name="description" 
                            value={formData.description} 
                            onChange={handleChange} 
                            className="admin-gallery-page-textarea" 
                            placeholder="Enter image caption or description..." 
                            rows="3" 
                        />
                    </div>
                </div>

                <div className="admin-gallery-page-modal-footer">
                    <button type="button" className="admin-gallery-page-modal-btn dismiss" onClick={onClose}>Cancel</button>
                    <button type="button" className="admin-gallery-page-modal-btn save" onClick={handleSubmit}>
                        <FaSave style={{ marginRight: '6px' }} /> Save Photo
                    </button>
                </div>
            </div>
        </div>
    );
};

/* =========================================================
   3. MAIN ADMIN GALLERY COMPONENT
========================================================= */
const AddGallery = () => {
    const [galleryData, setGalleryData] = useState([]); 
    const [searchQuery, setSearchQuery] = useState('');
    const [folderFilter, setFolderFilter] = useState('All');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); 
    const [selectedItem, setSelectedItem] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);

    const fetchGalleryData = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/gallery');
            const data = response.data;
            if (Array.isArray(data)) setGalleryData(data);
            else setGalleryData([]);
        } catch (error) {
            console.error("Error fetching gallery:", error);
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Failed to load gallery data.', 'error');
            }
            setGalleryData([]);
        }
    }, []);

    useEffect(() => { 
        fetchGalleryData(); 
    }, [fetchGalleryData]);

    // Only allow toggle if screen has collapsed columns (< 1200px)
    const toggleRow = (id) => {
        if (window.innerWidth < 1200) {
            setExpandedRow(prev => (prev === id ? null : id));
        }
    };
    
    const uniqueFolders = useMemo(() => {
        return [...new Set(galleryData.map(d => d.folderName).filter(Boolean))];
    }, [galleryData]);

    // Filtering
    const filteredGallery = useMemo(() => {
        return galleryData.filter(item => {
            if (folderFilter !== 'All' && item.folderName !== folderFilter) return false;
            if (searchQuery) {
                const term = searchQuery.toLowerCase();
                const fName = item.folderName ? item.folderName.toLowerCase() : '';
                const desc = item.description ? item.description.toLowerCase() : '';
                return fName.includes(term) || desc.includes(term);
            }
            return true;
        });
    }, [galleryData, searchQuery, folderFilter]);

    // Reset pagination on search or folder filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, folderFilter]);

    // Slice pagination data
    const paginatedGallery = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredGallery.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredGallery, currentPage, itemsPerPage]);

    // Save
    const handleSaveItem = async (formData) => {
        try {
            if (modalMode === 'edit') {
                await axiosInstance.put(`/api/gallery/${selectedItem._id}`, formData);
            } else {
                await axiosInstance.post('/api/gallery', formData);
            }

            await fetchGalleryData();
            Swal.fire({
                icon: 'success',
                title: modalMode === 'edit' ? 'Photo Updated' : 'Photo Uploaded',
                text: 'Gallery updated successfully.',
                confirmButtonColor: '#50A5C0'
            });
            setIsItemModalOpen(false);
        } catch (error) {
            console.error("Save Error:", error);
            if (error.response?.status !== 401) {
                Swal.fire('Error', error.response?.data?.message || 'Operation failed.', 'error');
            }
        }
    };

    // Delete
    const handleDeleteItem = (id) => {
        Swal.fire({ 
            title: 'Delete Photo?', 
            text: 'This image will be permanently removed.', 
            icon: 'warning', 
            showCancelButton: true, 
            confirmButtonColor: '#DC2626',
            cancelButtonColor: '#64748B',
            confirmButtonText: 'Yes, Delete'
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/gallery/${id}`);
                    await fetchGalleryData();
                    Swal.fire('Deleted', 'Image removed from gallery.', 'success');
                } catch (error) {
                    if (error.response?.status !== 401) {
                        Swal.fire('Error', error.response?.data?.message || 'Could not delete item.', 'error');
                    }
                }
            }
        });
    };

    // Toggle Active Status
    const toggleActive = async (id, currentStatus) => {
        try {
            await axiosInstance.put(`/api/gallery/${id}`, { isActive: !currentStatus });
            await fetchGalleryData();
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Could not update status.', 'error');
            }
        }
    };

    const openItemModal = (mode, item = null) => {
        setModalMode(mode);
        setSelectedItem(item);
        setIsItemModalOpen(true);
    };

    // Folder Actions (Rename / Delete)
    const performFolderAction = async (payload) => {
        try {
            const response = await axiosInstance.put('/api/gallery/folder', payload);
            await fetchGalleryData();
            Swal.fire('Success', response.data?.message || 'Folder updated successfully.', 'success');
            setIsToolsModalOpen(false);
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', error.response?.data?.message || 'Action failed.', 'error');
            }
        }
    };

    return (
        <>
            <Helmet>
                <title>Institutional Gallery Management | Nutrivio Admin</title>
                <meta name="description" content="Manage institutional gallery photos, descriptions, and albums." />
            </Helmet>

            <div className="admin-gallery-page-container">
                
                {/* Modals */}
                <GalleryFormModal 
                    isOpen={isItemModalOpen} 
                    onClose={() => setIsItemModalOpen(false)} 
                    data={selectedItem} 
                    mode={modalMode} 
                    onSave={handleSaveItem} 
                    existingFolders={uniqueFolders} 
                />

                <FolderToolsModal 
                    isOpen={isToolsModalOpen} 
                    onClose={() => setIsToolsModalOpen(false)} 
                    folders={uniqueFolders} 
                    onRename={(oldName, newName) => performFolderAction({ action: 'rename', oldName, newName })} 
                    onDelete={(folderName, deleteType) => performFolderAction({ action: 'delete', folderName, deleteType })} 
                />

                {/* Main Heading */}
                <h2 className="admin-gallery-page-title">Institutional Gallery &amp; Album Management</h2>

                {/* Single-Line Top Controls Row on Desktop */}
                <div className="admin-gallery-page-controls">
                    <div className="admin-gallery-page-search-wrapper">
                        <FaSearch className="admin-gallery-page-search-icon" />
                        <input
                            type="text"
                            className="admin-gallery-page-search-input"
                            placeholder="Search folder name, photo description..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="admin-gallery-page-action-group">
                        <select
                            value={folderFilter}
                            onChange={(e) => setFolderFilter(e.target.value)}
                            className="admin-gallery-page-select"
                        >
                            <option value="All">All Folders</option>
                            {uniqueFolders.map((f, i) => (
                                <option key={i} value={f}>{f}</option>
                            ))}
                        </select>

                        <select
                            value={itemsPerPage}
                            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                            className="admin-gallery-page-select"
                        >
                            {PAGE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>{opt} / page</option>
                            ))}
                        </select>

                        <button 
                            type="button" 
                            className="admin-gallery-page-btn tools" 
                            onClick={() => setIsToolsModalOpen(true)}
                            title="Manage / Rename / Delete Folders"
                        >
                            <FaCog /> Manage Folders
                        </button>

                        <button 
                            type="button" 
                            className="admin-gallery-page-btn add" 
                            onClick={() => openItemModal('add')}
                            title="Upload New Photo"
                        >
                            <FaPlus /> Add Photo
                        </button>
                    </div>
                </div>

                {/* Table Layout */}
                <div className="admin-gallery-page-table-wrapper">
                    <table className="admin-gallery-page-table">
                        <thead>
                            <tr>
                                <th className="col-sno">S.No</th>
                                <th className="col-folder admin-gallery-page-align-left">Folder Name</th>
                                <th className="col-thumb">Preview</th>
                                <th className="col-desc hide-lt-1200 admin-gallery-page-align-left">Description</th>
                                <th className="col-date hide-lt-991">Created At</th>
                                <th className="col-status hide-lt-768">Status</th>
                                <th className="col-action">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedGallery.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="admin-gallery-page-no-data">
                                        No gallery photos found.
                                    </td>
                                </tr>
                            ) : (
                                paginatedGallery.map((item, i) => {
                                    const globalIndex = (currentPage - 1) * itemsPerPage + i;
                                    const isRowOpen = expandedRow === item._id;

                                    return (
                                        <React.Fragment key={item._id}>
                                            <tr className={`admin-gallery-page-main-row ${isRowOpen ? 'row-expanded' : ''}`}>
                                                
                                                {/* 1. S.No */}
                                                <td className="col-sno">{globalIndex + 1}</td>

                                                {/* 2. Folder Name */}
                                                <td className="col-folder admin-gallery-page-align-left">
                                                    <div className="admin-gallery-page-name-wrapper" onClick={() => toggleRow(item._id)}>
                                                        <div className="admin-gallery-page-inst-cluster">
                                                            <span className="admin-gallery-page-inst-name">
                                                                <FaFolder className="admin-gallery-folder-icon" /> {item.folderName || 'General'}
                                                            </span>
                                                        </div>
                                                        {/* Expand toggle button only appears when columns are hidden (< 1200px) */}
                                                        <button type="button" className="admin-gallery-page-expand-btn" aria-label="Toggle Details">
                                                            {isRowOpen ? <FaChevronUp /> : <FaChevronDown />}
                                                        </button>
                                                    </div>
                                                </td>

                                                {/* 3. Preview Thumbnail */}
                                                <td className="col-thumb">
                                                    <div className="admin-gallery-page-table-thumb">
                                                        <img 
                                                            src={getImageUrl(item.image)} 
                                                            alt="Thumbnail" 
                                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/80x55?text=No+Image'; }}
                                                        />
                                                    </div>
                                                </td>

                                                {/* 4. Description (Visible on Desktop >= 1200px) */}
                                                <td className="col-desc hide-lt-1200 admin-gallery-page-align-left">
                                                    <span className="admin-gallery-page-desc-text">
                                                        {item.description || <span className="admin-gallery-text-muted">No description</span>}
                                                    </span>
                                                </td>

                                                {/* 5. Created Date (Visible on Desktop & Tablet >= 991px) */}
                                                <td className="col-date hide-lt-991">
                                                    <span className="admin-gallery-page-date-text">{formatDate(item.createdAt)}</span>
                                                </td>

                                                {/* 6. Status Switch (Visible on >= 768px) */}
                                                <td className="col-status hide-lt-768">
                                                    <div 
                                                        className={`admin-gallery-page-switch ${item.isActive ? 'active' : ''}`} 
                                                        onClick={() => toggleActive(item._id, item.isActive)}
                                                        title={item.isActive ? 'Active / Visible' : 'Hidden'}
                                                    >
                                                        <div className="admin-gallery-page-switch-handle"></div>
                                                    </div>
                                                </td>

                                                {/* 7. Action Buttons */}
                                                <td className="col-action">
                                                    <div className="admin-gallery-page-action-btns">
                                                        <button
                                                            type="button"
                                                            className="admin-gallery-page-icon-btn edit"
                                                            title="Edit Photo"
                                                            onClick={() => openItemModal('edit', item)}
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="admin-gallery-page-icon-btn delete"
                                                            title="Delete Photo"
                                                            onClick={() => handleDeleteItem(item._id)}
                                                        >
                                                            <FaTrashAlt />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Expanded Row Accordion: Only renders when screen is < 1200px and has hidden data */}
                                            {isRowOpen && (
                                                <tr className="admin-gallery-page-expanded-row">
                                                    <td colSpan="7">
                                                        <div className="admin-gallery-page-expanded-content">
                                                            
                                                            {/* Status (Shows on < 768px) */}
                                                            <div className="admin-gallery-page-exp-item show-exp-lt-768">
                                                                <span className="admin-gallery-page-exp-label">Published Status</span>
                                                                <div 
                                                                    className={`admin-gallery-page-switch ${item.isActive ? 'active' : ''}`} 
                                                                    onClick={() => toggleActive(item._id, item.isActive)}
                                                                >
                                                                    <div className="admin-gallery-page-switch-handle"></div>
                                                                </div>
                                                            </div>

                                                            {/* Created Date (Shows on < 991px) */}
                                                            <div className="admin-gallery-page-exp-item show-exp-lt-991">
                                                                <span className="admin-gallery-page-exp-label">Created At</span>
                                                                <span className="admin-gallery-page-exp-value">{formatDate(item.createdAt)}</span>
                                                            </div>

                                                            {/* Description (Shows on < 1200px) */}
                                                            <div className="admin-gallery-page-exp-item show-exp-lt-1200 admin-gallery-page-col-span-full">
                                                                <span className="admin-gallery-page-exp-label">Description / Caption</span>
                                                                <div className="admin-gallery-page-text-content">
                                                                    {item.description || 'No caption provided.'}
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination (Strictly below the table) */}
                {filteredGallery.length > 0 && (
                    <Pagination
                        totalItems={filteredGallery.length}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                )}

            </div>
        </>
    );
};

export default AddGallery;